import * as React from 'react'
import { Button, Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { EditOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { notification, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { legacyValidateFields } from '@halo/_utils/antd'
import * as deepEqual from 'fast-deep-equal'
import { delay } from '@halo/_utils/delay'
import * as randomstring from 'randomstring'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { IDomainEcommConfig } from '@halo/interfaces/domain-ecomm-config'
import { SimpleTimeframe } from '@halo/components/simple-timeframe/simple-timeframe'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'

interface IDomainEcommEditor extends FormComponentProps {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDEEState {}

export const DomainEcommEditor = Form.create<IDomainEcommEditor>()(
    class extends React.Component<IDomainEcommEditor, IDEEState> {
        public state: IDEEState = {}

        protected appState: AppState
        protected appService: AppService
        protected domainService: DomainService

        public constructor(props: IDomainEcommEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.domainService = Container.get(DomainService)

            executeCleanDrawerRender(this)
        }

        public render() {
            const currentValues = this.getCurrentValues()
            const config = this.domain.ecomm_config ?? {}

            return (
                <Drawer
                    className={classnames('ddf-ecomm-settings-editor')}
                    title="Update Ecomm Settings"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form>
                        <Well title="Attribution Windows" mode="ghost" hideFooter={true}>
                            <Form.Item className="click-through-window" label="Click-Through">
                                {this.props.form.getFieldDecorator('clickWindow', {
                                    initialValue: {
                                        seconds: (config.click_attribution_window_hours ?? 72) * 60 * 60,
                                        metric: 'hours',
                                    },
                                })(
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds', 'minutes', 'days']}
                                        onChange={async () =>
                                            delay(() => {
                                                this.forceUpdate()
                                            }, 0.05)
                                        }
                                    />,
                                )}
                            </Form.Item>

                            <Form.Item className="view-through-window" label="View-Through">
                                {this.props.form.getFieldDecorator('viewWindow', {
                                    initialValue: {
                                        seconds: (config.impression_attribution_window_hours ?? 0) * 60 * 60,
                                        metric: 'hours',
                                    },
                                })(
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds', 'minutes', 'days']}
                                        onChange={async () =>
                                            delay(() => {
                                                this.forceUpdate()
                                            }, 0.05)
                                        }
                                    />,
                                )}
                            </Form.Item>
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected get domain(): DomainDto {
            return this.props.domain
        }

        protected getCurrentValues(): Partial<DomainDto> {
            let values: Partial<DomainDto> = {}
            const formValues = this.props.form.getFieldsValue()

            values.ecomm_config = {} as IDomainEcommConfig
            values.ecomm_config.click_attribution_window_hours = (formValues.clickWindow?.seconds ?? 259_200) / 60 / 60
            values.ecomm_config.impression_attribution_window_hours = (formValues.viewWindow?.seconds ?? 0) / 60 / 60

            if (deepEqual(values.ecomm_config, this.domain.ecomm_config)) {
                delete values.ecomm_config
            }

            return values
        }

        protected handleSubmit = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const values = this.getCurrentValues()

                const { ok, data } = await this.domainService.patch(this.domain.id, values, {
                    showLoadingScreen: true,
                })

                if (ok) {
                    this.props.onDomainUpdated(data)
                    this.props.onClose(null)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }
    },
)

interface IDomainEcommWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDEWState {
    showDrawer: boolean
    drawerKey?: string
}

export class DomainEcommWell extends React.Component<IDomainEcommWell, IDEWState> {
    public state: IDEWState = {
        showDrawer: false,
    }

    protected readonly appState: AppState

    public constructor(props: IDomainEcommWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Ecomm Settings"
                actions={
                    this.appState.abilityStore.can(
                        AbilityAction.UPDATE,
                        asCaslSubject(SubjectEntity.DOMAIN, {
                            ...this.props.domain,
                            statusId: this.props.domain?.status_id,
                        }),
                    ) && (
                        <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>
                    )
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderAttributionWindows()}

                            <DomainEcommEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderAttributionWindows() {
        const config = this.domain.ecomm_config ?? {}

        return (
            <Well title="Attribution Windows" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Click-Through</div>
                    <div className={classnames('form-layout-row-value')}>
                        {config.click_attribution_window_hours ?? 72} hour(s)
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>View-Through</div>
                    <div className={classnames('form-layout-row-value')}>
                        {config.impression_attribution_window_hours ?? 0} hour(s)
                    </div>
                </div>
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
